import {ChangeEvent, FC, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {brandName, numberWithCommas} from '../../../../../../_metronic/helpers'
import {
  readTaxFolderReferences,
  registerTaxFolderReference,
  TaxFolderReferenceCandidate,
  TaxFolderReferenceResult,
} from '../core/_requests'
import {InvoiceSubject} from '../core/invoiceSubject'
import {FieldHelpLabel} from '../../../../../../_metronic/helpers/components/FieldHelp'

type Props = {
  subject: InvoiceSubject
  onClose: () => void
  onSelect: (candidate: TaxFolderReferenceCandidate) => void
}

const MAX_FILE_SIZE = 10 * 1024 * 1024

const SUBJECT_TITLES: Record<number, string> = {
  2: 'اصلاحی',
  3: 'ابطالی',
  4: 'برگشت از فروش',
}

// The Excel list export has no items: a sales return needs its reference's items.
const RETURN_NEEDS_ITEMS =
  'برای برگشت از فروش، اقلام صورتحساب مرجع لازم است و خروجی اکسل کارپوشه اقلام ندارد؛ PDF همین صورتحساب را با «ورود PDF تکی با اقلام» وارد کنید.'

const getRequestError = (error: any, fallback: string) => {
  const data = error?.response?.data
  return data?.Message || data?.message || (typeof data === 'string' ? data : '') || fallback
}

const TaxFolderReferenceModal: FC<Props> = ({subject, onClose, onSelect}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File>()
  const [result, setResult] = useState<TaxFolderReferenceResult>()
  const [loading, setLoading] = useState(false)
  const [registeringTaxId, setRegisteringTaxId] = useState<string>()
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const subjectTitle = SUBJECT_TITLES[subject] || 'ارجاعی'
  const busy = loading || Boolean(registeringTaxId)

  const candidates = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return result?.candidates || []
    return (result?.candidates || []).filter((item) =>
      [item.taxId, item.invoiceNo, item.buyerName, item.folderStatus]
        .filter(Boolean)
        .some((value) => value?.toLowerCase().includes(term))
    )
  }, [result, search])

  const chooseFile = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0]
    setError('')
    setResult(undefined)
    if (!nextFile) return
    if (!nextFile.name.toLowerCase().endsWith('.xlsx')) {
      setError('فقط فایل خروجی کارپوشه با فرمت xlsx قابل انتخاب است.')
      return
    }
    if (nextFile.size > MAX_FILE_SIZE) {
      setError('حجم فایل نباید بیشتر از ۱۰ مگابایت باشد.')
      return
    }
    setFile(nextFile)
  }

  const upload = () => {
    if (!file || busy) return
    setLoading(true)
    setError('')
    readTaxFolderReferences(file)
      .then(setResult)
      .catch((requestError) =>
        setError(
          getRequestError(
            requestError,
            'خواندن فایل خروجی کارپوشه انجام نشد. لطفاً دوباره تلاش کنید.'
          )
        )
      )
      .finally(() => setLoading(false))
  }

  const registerAndSelect = (candidate: TaxFolderReferenceCandidate) => {
    if (!file || busy) return
    setRegisteringTaxId(candidate.taxId)
    setError('')
    registerTaxFolderReference(file, candidate.taxId, subject)
      .then((registered) => {
        setRegisteringTaxId(undefined)
        const notice =
          subject === 2 && !registered.alreadyRegistered
            ? `${registered.message} اقلام صورتحساب اصلاحی را در فرم وارد کنید.`
            : registered.message
        onSelect({...candidate, ...registered.candidate, notice})
      })
      .catch((requestError) => {
        setRegisteringTaxId(undefined)
        setError(
          getRequestError(
            requestError,
            'ثبت سربرگ صورتحساب در پنل انجام نشد. لطفاً دوباره تلاش کنید.'
          )
        )
      })
  }

  const renderAction = (candidate: TaxFolderReferenceCandidate) => {
    if (candidate.canLoadDetails) {
      return (
        <button
          type='button'
          className='btn btn-sm btn-primary'
          onClick={() => onSelect(candidate)}
          disabled={busy}
        >
          انتخاب
        </button>
      )
    }
    if (subject === 4 || !candidate.canRegister) {
      return (
        <button type='button' className='btn btn-sm btn-light' disabled>
          انتخاب
        </button>
      )
    }
    const registering = registeringTaxId === candidate.taxId
    return (
      <button
        type='button'
        className='btn btn-sm btn-primary text-nowrap'
        onClick={() => registerAndSelect(candidate)}
        disabled={busy}
      >
        {registering && (
          <span className='spinner-border spinner-border-sm me-2' aria-hidden='true'></span>
        )}
        {registering ? 'در حال ثبت سربرگ…' : 'ثبت سربرگ و انتخاب'}
      </button>
    )
  }

  const renderNotInPanelNote = (candidate: TaxFolderReferenceCandidate) => {
    if (subject === 4) {
      return <div className='text-warning fs-8 mt-1'>{RETURN_NEEDS_ITEMS}</div>
    }
    if (!candidate.canRegister) {
      return (
        <div className='text-warning fs-8 mt-1'>
          {candidate.registerMessage ||
            'سربرگ این صورتحساب از این فایل قابل ثبت نیست؛ PDF همین صورتحساب را با «ورود PDF تکی با اقلام» وارد کنید.'}
        </div>
      )
    }
    return (
      <div className='text-gray-600 fs-8 mt-1'>
        در {brandName} نیست؛ با انتخاب، سربرگ و وضعیت آن از همین فایل در پنل ثبت می‌شود.
      </div>
    )
  }

  return (
    <Modal
      show
      onHide={busy ? () => undefined : onClose}
      centered
      size='xl'
      fullscreen='md-down'
      dialogClassName='mp-invoice-subdialog'
      backdrop={busy ? 'static' : true}
    >
      <Modal.Header>
        <div>
          <Modal.Title as='h2'>
            <FieldHelpLabel label='انتخاب صورتحساب مرجع' helpKey='header.irtaxid' />
          </Modal.Title>
          <div className='text-gray-500 fs-8 mt-1'>
            صدور صورتحساب {subjectTitle} از خروجی کارپوشه مؤدیان
          </div>
        </div>
        <button
          type='button'
          className='btn btn-sm btn-icon btn-light'
          onClick={onClose}
          aria-label='بستن'
          disabled={busy}
        >
          <i className='las la-times fs-2'></i>
        </button>
      </Modal.Header>
      <Modal.Body>
        {!result ? (
          <>
            <div className='mp-reference-upload-note'>
              <i className='las la-info-circle'></i>
              <span>
                خروجی اکسل بخش صورتحساب‌های کارپوشه را انتخاب کنید. اگر صورتحساب انتخاب‌شده در{' '}
                {brandName} باشد، اطلاعات و اقلامش از همان‌جا بارگذاری می‌شود؛ اگر نباشد، سربرگ و
                وضعیت آن از همین فایل در پنل ثبت می‌شود. این خروجی اقلام ندارد.
              </span>
            </div>
            <div className='mp-reference-upload'>
              <input
                ref={inputRef}
                type='file'
                accept='.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                onChange={chooseFile}
                disabled={busy}
              />
              <i className='las la-file-excel'></i>
              <div className='flex-grow-1 min-w-0'>
                <strong>
                  <FieldHelpLabel
                    label={file?.name || 'فایل xlsx خروجی کارپوشه را انتخاب کنید'}
                    helpKey='ui.referenceFile'
                  />
                </strong>
                <span>
                  {file
                    ? `${numberWithCommas(Math.ceil(file.size / 1024))} کیلوبایت`
                    : 'حداکثر حجم فایل ۱۰ مگابایت'}
                </span>
              </div>
              <button
                type='button'
                className='btn btn-light-primary'
                onClick={() => inputRef.current?.click()}
                disabled={busy}
              >
                انتخاب فایل
              </button>
            </div>
          </>
        ) : (
          <>
            <div className='d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4'>
              <div>
                <strong>{numberWithCommas(result.candidates.length)} صورتحساب قابل انتخاب</strong>
                <div className='text-gray-500 fs-8'>{result.fileName}</div>
              </div>
              <div className='w-100 mw-350px'>
                <label className='form-label fw-semibold'>
                  <FieldHelpLabel label='جستجوی صورتحساب مرجع' helpKey='ui.referenceSearch' />
                </label>
                <div className='position-relative'>
                  <i className='las la-search position-absolute top-50 translate-middle-y ms-4 fs-3 text-gray-500'></i>
                  <input
                    className='form-control ps-12'
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder='جستجو در شناسه، شماره یا خریدار'
                  />
                </div>
              </div>
            </div>
            <div className='mp-reference-table-wrap'>
              <table className='table align-middle mb-0'>
                <thead>
                  <tr>
                    <th><FieldHelpLabel label='شناسه مالیاتی' helpKey='header.irtaxid' /></th>
                    <th><FieldHelpLabel label='اطلاعات صورتحساب مرجع' helpKey='section.header' /></th>
                    <th><FieldHelpLabel label='وضعیت کارپوشه' /></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((candidate) => (
                    <tr key={candidate.taxId}>
                      <td className='englishText fw-semibold'>{candidate.taxId}</td>
                      <td>
                        {candidate.canLoadDetails ? (
                          <>
                            <strong>شماره {candidate.invoiceNo || '-'}</strong>
                            <div className='text-gray-500 fs-8'>
                              {candidate.invoiceDate || ''}
                              {candidate.buyerName ? ` — ${candidate.buyerName}` : ''}
                            </div>
                          </>
                        ) : (
                          <>
                            <strong>
                              {[candidate.subjectTitle, candidate.invoiceTypeTitle]
                                .filter(Boolean)
                                .join(' · ') || 'صورتحساب کارپوشه'}
                            </strong>
                            <div className='text-gray-500 fs-8'>
                              {candidate.invoiceDate || ''}
                              {candidate.buyerName ? ` — ${candidate.buyerName}` : ''}
                              {typeof candidate.netPrice === 'number'
                                ? ` — ${numberWithCommas(candidate.netPrice)} ریال`
                                : ''}
                            </div>
                            {renderNotInPanelNote(candidate)}
                          </>
                        )}
                      </td>
                      <td>{candidate.folderStatus || '-'}</td>
                      <td className='text-end'>{renderAction(candidate)}</td>
                    </tr>
                  ))}
                  {candidates.length === 0 && (
                    <tr>
                      <td colSpan={4} className='text-center text-gray-500 py-10'>
                        صورتحسابی با این عبارت پیدا نشد.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
        {error && (
          <div className='alert alert-danger mt-4 mb-0' role='alert'>
            {error}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={onClose} disabled={busy}>
          انصراف
        </button>
        {!result && (
          <button
            type='button'
            className='btn btn-primary'
            onClick={upload}
            disabled={!file || busy}
          >
            {loading ? 'در حال خواندن فایل…' : 'نمایش صورتحساب‌ها'}
          </button>
        )}
        {result && (
          <button
            type='button'
            className='btn btn-light-primary'
            disabled={busy}
            onClick={() => {
              setResult(undefined)
              setFile(undefined)
              setError('')
              if (inputRef.current) inputRef.current.value = ''
            }}
          >
            فایل دیگر
          </button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export {TaxFolderReferenceModal}
